import type { JobSummaryViewModel } from '@/features/job-improved-selection';
import type { BulkCandidate, RjCommBulkResult } from '@/widgets/rj-comm-bulk';
import type { InterviewType } from '@/features/nylas-calendar-manage-connection-modal';
import type { Vacancy } from 'clients/AutoCompleteClient/types';
import type { StatusChangeFeedbackParams } from 'clients/CampaignsClient/CampaignsClient.types';
import type { Candidate } from 'model/Candidate/Candidate';
import type {
  CampaignPhoneReachOutDialogConfig,
  CampaignPhoneReachOutDialogData,
} from 'shared/legacy/contexts/DialogContext/dialogs/CampaignPhoneReachOutDialog/CampaignPhoneReachOutDialog.types';
import type { DiscardChangesModalData } from 'shared/legacy/contexts/DialogContext/modals/DiscardChangesModal/DiscardChangesModal.types';
import type { EnrichVacancyModalData } from 'shared/legacy/contexts/DialogContext/modals/EnrichVacancyModal/EnrichVacancyModal.types';
import type { SelectAtsVacancyModalType } from 'shared/legacy/contexts/DialogContext/modals/SelectAtsVacancyForCampaignModal/SelectAtsVacancyForCampaignModal.types';
import type { OutreachType } from 'types/outreach';

export enum GlobalDialogType {
  ShouldConnectEmail = 'ShouldConnectEmail',
  CampaignPhoneReachOut = 'CampaignPhoneReachOut',
  CandidateOutreach = 'CandidateOutreach',
  EnrichVacancy = 'EnrichVacancy',
  CandidateFeedback = 'CandidateFeedback',
  DiscardChangesModal = 'DiscardChangesModal',
  SelectAtsVacancyForCampaign = 'SelectAtsVacancyForCampaign',
  ReachoutInterviewModal = 'ReachoutInterviewModal',
  NylasCalendarManageConnectionModal = 'NylasCalendarManageConnectionModal',
  RejectCandidate = 'RejectCandidate',
  RjCommBulk = 'RjCommBulk',
}

export type RjCommBulkDialogData = {
  campaignId: number;
  candidates: BulkCandidate[];
  initialJob?: JobSummaryViewModel;
  onComplete?: (result: RjCommBulkResult) => void;
  onRemoveCandidate?: (esPersonId: string) => void;
};

export type CandidateOutreachDialogData = {
  candidate: Candidate;
  outreachType: OutreachType;
  initialVacancy?: Vacancy;
};

export type CandidateFeedbackDialogData = {
  candidate: Candidate;
  onSubmit: (status_change_feedback?: StatusChangeFeedbackParams) => void;
};

export type SelectAtsVacancyForCampaignDialogData = {
  candidateFirstName?: string;
  atsName?: string;
  type?: SelectAtsVacancyModalType;
  onConfirm?: (vacancy: Vacancy) => Promise<void> | void;
  onClose?: () => void;
  onCloseButtonClick?: () => void;
};

export type ReachoutInterviewModalData = {
  esPersonId: string;
  onInterviewBooked?: () => Promise<void> | void;
};

export type RejectCandidateDialogData = {
  esPersonId: string;
  campaignId: number;
  candidateName: string;
  improvedJobId?: number;
  onSuccess?: () => void;
};

export type NylasCalendarManageConnectionModalData = {
  onFinish?: (interviewType: InterviewType) => void;
  onCancel?: () => void;
  skipManageConnection?: boolean;
  initialInterviewType?: InterviewType;
  footer?: React.ReactNode;
};

export type GlobalDialogDataDefinition = {
  [GlobalDialogType.ShouldConnectEmail]: never;
  [GlobalDialogType.CampaignPhoneReachOut]: CampaignPhoneReachOutDialogData;
  [GlobalDialogType.CandidateOutreach]: CandidateOutreachDialogData;
  [GlobalDialogType.EnrichVacancy]: EnrichVacancyModalData;
  [GlobalDialogType.CandidateFeedback]: CandidateFeedbackDialogData;
  [GlobalDialogType.DiscardChangesModal]: DiscardChangesModalData;
  [GlobalDialogType.SelectAtsVacancyForCampaign]: SelectAtsVacancyForCampaignDialogData;
  [GlobalDialogType.ReachoutInterviewModal]: ReachoutInterviewModalData;
  [GlobalDialogType.NylasCalendarManageConnectionModal]: NylasCalendarManageConnectionModalData;
  [GlobalDialogType.RejectCandidate]: RejectCandidateDialogData;
  [GlobalDialogType.RjCommBulk]: RjCommBulkDialogData;
};

export type GlobalDialogConfigDefinition = {
  [GlobalDialogType.ShouldConnectEmail]: null;
  [GlobalDialogType.CampaignPhoneReachOut]: CampaignPhoneReachOutDialogConfig;
  [GlobalDialogType.CandidateOutreach]: null;
  [GlobalDialogType.EnrichVacancy]: null;
  [GlobalDialogType.CandidateFeedback]: null;
  [GlobalDialogType.DiscardChangesModal]: null;
  [GlobalDialogType.SelectAtsVacancyForCampaign]: null;
  [GlobalDialogType.ReachoutInterviewModal]: null;
  [GlobalDialogType.NylasCalendarManageConnectionModal]: null;
  [GlobalDialogType.RejectCandidate]: null;
  [GlobalDialogType.RjCommBulk]: null;
};

export type GlobalDialogData<T extends GlobalDialogType> = GlobalDialogDataDefinition[T];

export type GlobalDialogConfig<T extends GlobalDialogType> = GlobalDialogConfigDefinition[T];

export type OpenGlobalDialogOptions<T extends GlobalDialogType> = {
  payload: GlobalDialogData<T>;
  key?: string;
};

export type StoredDialogData = {
  [K in GlobalDialogType]?: {
    [key: string]: GlobalDialogDataDefinition[K];
  };
};

export type ActiveDialog = {
  type: GlobalDialogType;
  key: string;
};

export interface GlobalDialogsStore {
  config: GlobalDialogConfigDefinition;
  dialogs: StoredDialogData;
  activeDialogs: ActiveDialog[];

  setActiveDialogs: (dialogs: ActiveDialog[]) => void;
  addActiveDialog: (dialog: ActiveDialog) => void;
  removeActiveDialog: (dialogType: GlobalDialogType) => void;
  setGlobalDialogConfig: <T extends GlobalDialogType>(type: T, config: GlobalDialogConfig<T>) => void;
  getGlobalDialogConfig: <T extends GlobalDialogType>(type: T) => GlobalDialogConfig<T>;
  set: <T extends GlobalDialogType>(type: T, data: GlobalDialogDataDefinition[T], key: string) => void;
  get: <T extends GlobalDialogType>(type: T, key: string) => GlobalDialogDataDefinition[T] | undefined;
  getTypeData: <T extends GlobalDialogType>(type: T) => { [key: string]: GlobalDialogDataDefinition[T] } | undefined;
  remove: <T extends GlobalDialogType>(type: T, key: string) => void;
}
